import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  FileSignature,
  Search,
  Eye,
  CheckCircle2,
  Clock,
  XCircle,
  Download,
  Filter,
  Building2,
  Calendar,
  User,
  FileText
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminContracts() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedContract, setSelectedContract] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const res = await axios.get(`${API}/contracts`);
      // Enrich with project info
      const enriched = await Promise.all(res.data.map(async (contract) => {
        try {
          const projectRes = await axios.get(`${API}/projects/${contract.project_id}`);
          return { ...contract, project: projectRes.data };
        } catch {
          return contract;
        }
      }));
      setContracts(enriched);
    } catch (err) {
      console.error("Error fetching contracts:", err);
      toast.error("Failed to load contracts");
    } finally {
      setLoading(false);
    }
  };

  const viewContract = (contract) => {
    setSelectedContract(contract);
    setDialogOpen(true);
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    signed: "bg-green-500/20 text-green-400 border-green-500/30",
    cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
    expired: "bg-gray-500/20 text-gray-400 border-gray-500/30",
  };

  const statusIcons = {
    pending: <Clock className="w-4 h-4" />,
    signed: <CheckCircle2 className="w-4 h-4" />,
    cancelled: <XCircle className="w-4 h-4" />,
    expired: <XCircle className="w-4 h-4" />,
  };

  const filteredContracts = contracts.filter(c => {
    const matchesSearch = 
      c.contract_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.project?.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || c.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const stats = {
    total: contracts.length,
    pending: contracts.filter(c => c.status === "pending").length,
    signed: contracts.filter(c => c.status === "signed").length,
  };

  return (
    <DashboardLayout navItems={adminNavItems} title="Contracts">
      <div className="space-y-6" data-testid="admin-contracts-page">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <FileSignature className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-3xl font-['Unbounded'] font-bold text-blue-400">{stats.total}</p>
                <p className="text-sm text-[#94A3B8]">Total Contracts</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-yellow-500/20 rounded-lg">
                <Clock className="w-6 h-6 text-yellow-400" />
              </div>
              <div>
                <p className="text-3xl font-['Unbounded'] font-bold text-yellow-400">{stats.pending}</p>
                <p className="text-sm text-[#94A3B8]">Pending</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-6 flex items-center gap-4">
              <div className="p-3 bg-green-500/20 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <p className="text-3xl font-['Unbounded'] font-bold text-green-400">{stats.signed}</p>
                <p className="text-sm text-[#94A3B8]">Signed</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94A3B8]" />
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search contracts or projects..."
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-[#64748B]"
              data-testid="search-contracts"
            />
          </div>
          <div className="flex gap-2">
            {["all", "pending", "signed", "cancelled"].map(status => (
              <Button
                key={status}
                variant={statusFilter === status ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter(status)}
                className={statusFilter === status 
                  ? "bg-[#23F7DD] text-[#050505]" 
                  : "border-white/20 text-[#94A3B8]"
                }
              >
                {status === "all" ? "All" : status === "pending" ? "Pending" : status === "signed" ? "Signed" : "Cancelled"}
              </Button>
            ))}
          </div>
        </div>

        {/* Contracts Table */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="w-10 h-10 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredContracts.length === 0 ? (
              <div className="text-center py-12">
                <FileSignature className="w-12 h-12 mx-auto text-[#475569] mb-4" />
                <p className="text-[#94A3B8]">No contracts yet</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-[#94A3B8]">Contract ID</TableHead>
                    <TableHead className="text-[#94A3B8]">Project</TableHead>
                    <TableHead className="text-[#94A3B8]">Status</TableHead>
                    <TableHead className="text-[#94A3B8]">Created</TableHead>
                    <TableHead className="text-[#94A3B8]">Signed By</TableHead>
                    <TableHead className="text-[#94A3B8] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContracts.map((contract) => (
                    <TableRow key={contract.contract_id} className="border-white/10">
                      <TableCell className="font-mono text-white">
                        {contract.contract_id?.slice(0, 16)}...
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#94A3B8]" />
                          <span className="text-white">{contract.project?.title || "N/A"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statusColors[contract.status] || statusColors.pending}>
                          {statusIcons[contract.status]}
                          <span className="ml-1">
                            {contract.status === "pending" ? "În așteptare" : 
                             contract.status === "signed" ? "Semnat" : contract.status}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="text-[#94A3B8]">
                        {contract.created_at ? new Date(contract.created_at).toLocaleDateString("ro-RO") : "-"}
                      </TableCell>
                      <TableCell>
                        {contract.signer_name ? (
                          <div className="flex items-center gap-2 text-white">
                            <User className="w-4 h-4 text-green-400" />
                            {contract.signer_name}
                          </div>
                        ) : (
                          <span className="text-[#64748B]">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => viewContract(contract)}
                          className="text-[#23F7DD] hover:text-[#70FFEF] hover:bg-[#23F7DD]/10"
                          data-testid={`view-contract-${contract.contract_id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Vezi
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Contract View Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-['Unbounded'] flex items-center gap-2">
                <FileSignature className="w-5 h-5 text-[#23F7DD]" />
                Contract de Servicii
              </DialogTitle>
            </DialogHeader>
            {selectedContract && (
              <div className="space-y-6">
                {/* Contract Meta */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-white/5 rounded-lg">
                  <div>
                    <p className="text-xs text-[#94A3B8]">Contract ID</p>
                    <p className="text-white font-mono">{selectedContract.contract_id}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Status</p>
                    <Badge className={statusColors[selectedContract.status]}>
                      {selectedContract.status === "pending" ? "În așteptare" : 
                       selectedContract.status === "signed" ? "Semnat" : selectedContract.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Proiect</p>
                    <p className="text-white">{selectedContract.project?.title || "N/A"}</p>
                  </div>
                  <div>
                    <p className="text-xs text-[#94A3B8]">Creat la</p>
                    <p className="text-white">
                      {selectedContract.created_at 
                        ? new Date(selectedContract.created_at).toLocaleString("ro-RO")
                        : "-"}
                    </p>
                  </div>
                </div>

                {/* Contract Text */}
                <div className="bg-white/5 rounded-lg p-6">
                  <pre className="whitespace-pre-wrap text-[#94A3B8] text-sm font-mono leading-relaxed">
                    {selectedContract.contract_text}
                  </pre>
                </div>

                {/* Signature Info */}
                {selectedContract.status === "signed" && (
                  <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <h4 className="text-green-400 font-semibold mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-5 h-5" />
                      Contract Semnat
                    </h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-[#94A3B8]">Semnat de:</p>
                        <p className="text-white">{selectedContract.signer_name}</p>
                      </div>
                      <div>
                        <p className="text-[#94A3B8]">Data semnării:</p>
                        <p className="text-white">
                          {selectedContract.signed_at 
                            ? new Date(selectedContract.signed_at).toLocaleString("ro-RO")
                            : "-"}
                        </p>
                      </div>
                      <div>
                        <p className="text-[#94A3B8]">IP:</p>
                        <p className="text-white font-mono">{selectedContract.signer_ip || "-"}</p>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    className="border-white/20 text-white"
                    onClick={() => setDialogOpen(false)}
                  >
                    Închide
                  </Button>
                  <Button className="bg-[#23F7DD] text-[#050505] hover:bg-[#70FFEF]">
                    <Download className="w-4 h-4 mr-2" />
                    Descarcă PDF
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
